import React, { useState, useEffect } from "react";
import { Search, BookOpen } from "lucide-react";
import ResearchDisclosureGate from "./ResearchDisclosureGate";
import MethodPage from "./MethodPage";
import TickerSearchBar from "./TickerSearchBar";
import StockResearchModal from "./StockResearchModal";
import WatchlistGrid from "./WatchlistGrid";

/**
 * ResearchTab — Top-level container for the Research feature.
 *
 * Phase 1 SKELETON. Real modules wire in over the next phases:
 *   Phase 2: Disclosure gate (Firestore-backed) wraps everything below
 *   Phase 3: Module A — Analyst Ratings by Sector
 *   Phase 4: Method page (scorecard, glossary, risk methodology)
 *   Phase 5: Risk archetype connection from Risk Assessment results
 *
 * COMPLIANCE — IMPORTANT.
 * Nothing in this app may say "buy", "sell", "recommend", "pick", or
 * "you should". All Buy/Hold/Sell language in the analyst module must  // compliance-allow
 * be attributed: "[Analyst] at [Firm] rates this [stance]." Risk
 * character is ARCrypto's objective bucket, NOT a quality judgment.
 *
 * The persistent footer disclaimer is rendered on every research screen
 * by this container — sub-modules inherit it automatically.
 */

// Post-Chunk-C+ the Research tab has only two views:
//   watchlist — the default landing surface: search bar on top + your
//               saved tickers as cards (or empty-state + starter chips)
//   method    — the methodology explainer (Risk Character buckets,
//               conviction bands, etc.)
// AnalystRatingsModule (mock cards) was removed entirely.
const SUBVIEWS = [
  { id: "watchlist", label: "My Watchlist", icon: Search },
  { id: "method",    label: "Method",       icon: BookOpen },
];

const PersistentFooter = () => (
  <div className="mt-8 rounded-xl border border-arc-line bg-arc-surface/40 p-3 sm:p-4">
    <p className="text-[11px] sm:text-xs text-arc-dim leading-relaxed text-center">
      Educational research only. Not investment advice. Not personalized.
      ARCrypto is not a registered investment adviser.
    </p>
  </div>
);

const ResearchTab = ({ initialFilter, onFilterConsumed }) => {
  const [activeView, setActiveView] = useState("watchlist");

  // Legacy: deep-links from Risk Assessment used to land in the
  // analyst module. Now they land on the watchlist view (which is
  // where users actually live). The filter is consumed but no
  // longer used — left in for backward compat in case other parts
  // of the app still pass it.
  useEffect(() => {
    if (initialFilter) {
      setActiveView("watchlist");
      onFilterConsumed && onFilterConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  // Ticker the StockResearchModal is currently showing. null = modal
  // closed. Set by TickerSearchBar's onSubmit AND by WatchlistGrid's
  // onOpenTicker. We render the modal as a sibling so it portals into
  // <body> and isn't constrained by this tab's container layout.
  const [modalTicker, setModalTicker] = useState(null);

  // Disclosure gate. Until the user acknowledges (Firestore-persisted),
  // they see ONLY the gate — no content, no subview tabs, nothing.
  const [disclosureAck, setDisclosureAck] = useState(null);
  if (!disclosureAck) {
    return (
      <ResearchDisclosureGate
        onAcknowledge={(record) => setDisclosureAck(record)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="surface-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <Search size={16} className="text-arc-gold" />
          <p className="eyebrow">Research</p>
        </div>
        <h2 className="headline text-2xl mb-2">
          Grounded equity research for ARCrypto members
        </h2>
        <p className="text-arc-muted text-sm leading-relaxed max-w-3xl">
          Search any U.S.-listed ticker. Get a 9-section AI analysis
          grounded in SEC filings — no hallucinated numbers, every
          figure traces back to the source. Save tickers to your
          watchlist for one-click access whenever you come back.
        </p>
      </div>

      {/* Universal ticker lookup — powered by the arc-research-proxy
          Worker (EDGAR + Claude + Firestore cache). Hand-off to the
          modal on submit. */}
      <TickerSearchBar onSubmit={(t) => setModalTicker(t)} />

      {/* Subview tabs */}
      <div className="flex flex-wrap gap-2">
        {SUBVIEWS.map((v) => {
          const Icon = v.icon;
          const active = activeView === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setActiveView(v.id)}
              className={`btn-tactile flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all ${
                active
                  ? "btn-gold"
                  : "btn-outline border-arc-line hover:border-arc-gold/40"
              }`}
            >
              <Icon size={14} />
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Active subview */}
      {activeView === "watchlist" && (
        <WatchlistGrid onOpenTicker={(t) => setModalTicker(t)} />
      )}
      {activeView === "method" && <MethodPage />}

      <PersistentFooter />

      {/* StockResearchModal — renders only when modalTicker is set.
          It portals to document.body so position/overflow concerns
          in this container don't affect it. */}
      {modalTicker && (
        <StockResearchModal
          ticker={modalTicker}
          onClose={() => setModalTicker(null)}
        />
      )}
    </div>
  );
};

export default ResearchTab;
